import PropTypes from "prop-types";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import { AI_ASSISTANT_BLUE } from "./constants";

const iconSx = { color: "ink.soft", "&:hover": { color: AI_ASSISTANT_BLUE, bgcolor: "rgba(0,97,254,0.08)" } };

export default function AIChatHeader({
  sectionLabel,
  conversationTitle,
  onClose,
  onNewConversation,
  onToggleHistory,
  historyOpen,
  disabled,
  conversationLimitReached,
}) {
  // Starting a new conversation never loses anything (history keeps the old one,
  // unsent input is carried over), so there is no confirm step.
  return (
    <Box
      component="header"
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: "1px solid rgba(148,163,184,0.25)",
        bgcolor: "background.card",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar
          sx={{
            width: 32,
            height: 32,
            background: `linear-gradient(135deg, ${AI_ASSISTANT_BLUE}, #3b82f6)`,
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            fontWeight={700}
            sx={{ fontSize: 14.5, color: "ink.strong", lineHeight: 1.2 }}
          >
            Microlab AI Assistant
          </Typography>
          <Typography
            noWrap
            title={conversationTitle ? `${sectionLabel} · ${conversationTitle}` : sectionLabel}
            sx={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.35 }}
          >
            <Box component="span" sx={{ color: AI_ASSISTANT_BLUE, fontWeight: 700 }}>
              {sectionLabel}
            </Box>
            {conversationTitle ? ` · ${conversationTitle}` : ""}
          </Typography>
        </Box>
        <Tooltip title="New conversation">
          <span>
            <IconButton
              aria-label="Start new conversation"
              size="small"
              disabled={disabled}
              onClick={() => onNewConversation()}
              sx={
                conversationLimitReached
                  ? { color: "#fff", bgcolor: `${AI_ASSISTANT_BLUE} !important` }
                  : iconSx
              }
            >
              <AddCommentOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={historyOpen ? "Back to chat" : "Conversation history"}>
          <IconButton
            aria-label="Conversation history"
            aria-pressed={historyOpen}
            size="small"
            onClick={onToggleHistory}
            sx={historyOpen ? { color: AI_ASSISTANT_BLUE, bgcolor: "rgba(0,97,254,0.1)" } : iconSx}
          >
            <HistoryRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Minimize">
          <IconButton aria-label="Close AI assistant" size="small" onClick={onClose} sx={iconSx}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

AIChatHeader.propTypes = {
  sectionLabel: PropTypes.string,
  conversationTitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onNewConversation: PropTypes.func.isRequired,
  onToggleHistory: PropTypes.func.isRequired,
  historyOpen: PropTypes.bool,
  disabled: PropTypes.bool,
  conversationLimitReached: PropTypes.bool,
};
